import classNames from 'classnames';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';

/** Search-as-you-type input with a leading magnifier. */
export const SearchInput = ({
  value,
  onChange,
  placeholder,
  label,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  label: string;
}) => (
  <label className="relative block w-full md:max-w-sm">
    <span className="sr-only">{label}</span>
    <svg viewBox="0 0 16 16" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-faint" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden>
      <circle cx="7" cy="7" r="4.5" />
      <path d="M10.5 10.5L14 14" />
    </svg>
    <input
      type="search"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="h-11 w-full rounded-lg bg-control pl-9 pr-3 text-base text-fg shadow-ctl placeholder:text-fg-faint focus:outline-none focus:ring-2 focus:ring-brand/50 md:h-9 md:text-sm"
    />
  </label>
);

/** A native select styled like the other controls. */
export const FilterSelect = ({
  value,
  onChange,
  label,
  children,
  idle = '',
}: {
  value: string;
  onChange: (v: string) => void;
  label: string;
  children: ReactNode;
  /** The value that filters nothing ("all"), shown untinted. */
  idle?: string;
}) => (
  // On a phone filters share their row, each as wide as its share of it, so a filter row stays
  // short; one that would be narrower than 7.5rem wraps to the next row instead.
  <label className="block max-md:min-w-[7.5rem] max-md:flex-1">
    <span className="sr-only">{label}</span>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={classNames(
        'h-11 max-w-full rounded-lg pl-3 pr-8 text-base shadow-ctl focus:outline-none focus:ring-2 focus:ring-brand/50 max-md:w-full md:h-9 md:text-sm',
        // A filter in use is tinted, not outlined.
        value !== idle ? 'bg-control-on text-fg-strong' : 'bg-control text-fg-muted',
      )}
    >
      {children}
    </select>
  </label>
);

const CHECK = (
  <svg viewBox="0 0 16 16" className="h-4 w-4 shrink-0 text-brand" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M3.5 8.5l3 3 6-7" />
  </svg>
);

/**
 * A filter that takes several values: a button like FilterSelect's that opens a list to
 * tick them in, its first row filtering nothing. The list floats above everything
 * (elevation 4), as the Combobox's does, and closes on Escape, or on a click or a tab
 * away from it.
 */
export const FilterMultiSelect = ({
  value,
  onChange,
  label,
  allLabel,
  options,
}: {
  value: string[];
  onChange: (v: string[]) => void;
  label: string;
  /** The first row, and what the button says with nothing ticked, such as "All segments". */
  allLabel: string;
  options: { value: string; label: string }[];
}) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', away);
    return () => document.removeEventListener('pointerdown', away);
  }, [open]);
  const picked = options.filter((o) => value.includes(o.value));
  const row = 'flex min-h-11 w-full cursor-pointer items-center justify-between gap-3 rounded-md px-3 text-left text-base hover:bg-surface-hover md:min-h-9 md:text-sm';
  return (
    // On a phone it shares the filter row as FilterSelect does.
    <div
      ref={wrapRef}
      className="relative max-md:min-w-[7.5rem] max-md:flex-1"
      onBlur={(e) => {
        if (!wrapRef.current?.contains(e.relatedTarget as Node | null)) setOpen(false);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && open) {
          e.stopPropagation();
          setOpen(false);
        }
      }}
    >
      <button
        type="button"
        aria-label={label}
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((o) => !o)}
        className={classNames(
          'relative block h-11 max-w-full truncate rounded-lg pl-3 pr-8 text-left text-base shadow-ctl focus:outline-none focus:ring-2 focus:ring-brand/50 max-md:w-full md:h-9 md:max-w-[16rem] md:text-sm',
          // A filter in use is tinted, not outlined.
          value.length ? 'bg-control-on text-fg-strong' : 'bg-control text-fg-muted',
        )}
      >
        {picked.length ? picked.map((o) => o.label).join(', ') : allLabel}
        <svg viewBox="0 0 16 16" className="pointer-events-none absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-faint" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d={open ? 'M4 10l4-4 4 4' : 'M4 6l4 4 4-4'} />
        </svg>
      </button>
      {open && (
        <div
          id={listId}
          role="group"
          aria-label={label}
          // Keep the focus where it is, as the Combobox does: otherwise the blur closes the list before the click.
          onMouseDown={(e) => e.preventDefault()}
          className="absolute inset-x-0 top-full z-30 mt-1.5 max-h-72 animate-drop-in overflow-y-auto rounded-lg bg-surface-raised p-1 shadow-e4 md:right-auto md:w-max md:min-w-full md:max-w-xs"
        >
          <button type="button" className={classNames(row, 'text-fg-muted focus:bg-surface-hover focus:outline-none')} onClick={() => onChange([])}>
            {allLabel}
            {value.length === 0 && CHECK}
          </button>
          {options.map((o) => {
            const on = value.includes(o.value);
            return (
              <label key={o.value} className={classNames(row, 'focus-within:bg-surface-hover', on ? 'text-fg-strong' : 'text-fg')}>
                <input type="checkbox" className="sr-only" checked={on} onChange={() => onChange(on ? value.filter((x) => x !== o.value) : [...value, o.value])} />
                <span className="min-w-0 truncate">{o.label}</span>
                {on && CHECK}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};

/** Two or more mutually exclusive options shown as one segmented control. */
export const Segmented = <T extends string>({
  value,
  onChange,
  options,
  label,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
  label: string;
}) => (
  <div role="radiogroup" aria-label={label} className="inline-flex h-12 max-w-full overflow-x-auto overflow-y-hidden rounded-lg bg-layer p-0.5 shadow-well md:h-9">
    {options.map((o) => (
      <button
        key={o.value}
        type="button"
        role="radio"
        aria-checked={value === o.value}
        onClick={() => onChange(o.value)}
        className={classNames(
          'whitespace-nowrap rounded-md px-3 text-sm transition-colors',
          // The chosen option rises out of the sunken track.
          value === o.value ? 'bg-control text-fg-strong shadow-ctl' : 'text-fg-muted hover:text-fg',
        )}
      >
        {o.label}
      </button>
    ))}
  </div>
);

/** A centered message for empty, error and first-load states. */
export const StateMessage = ({ title, children, tone = 'neutral' }: { title: string; children?: ReactNode; tone?: 'neutral' | 'error' }) => (
  <div className="flex flex-col items-center justify-center rounded-card bg-surface px-6 py-16 text-center shadow-e1">
    <p className={classNames('text-sm font-semibold', tone === 'error' ? 'text-danger' : 'text-fg-strong')}>{title}</p>
    {children && <div className="mt-1 max-w-md text-sm text-fg-muted">{children}</div>}
  </div>
);

/** The flask that marks made-up data. */
export const FlaskIcon = ({ className = 'h-3.5 w-3.5' }: { className?: string }) => (
  <svg viewBox="0 0 16 16" className={className} fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden>
    <path d="M6 2v4L2.5 12.5A1 1 0 0 0 3.4 14h9.2a1 1 0 0 0 .9-1.5L10 6V2M5 2h6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/** Marks numbers that are not real yet (mock metrics, demo seed). */
export const DemoPill = ({ children, title }: { children: ReactNode; title: string }) => (
  <span title={title} className="pill inline-flex items-center gap-1.5 rounded-pill px-2.5 py-1 text-xs text-fg-muted">
    <FlaskIcon />
    {children}
  </span>
);

/** An on/off switch. The state shows in the track colour and the knob position. */
export const Toggle = ({ checked, onChange, label, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={(e) => {
      e.stopPropagation();
      onChange(!checked);
    }}
    className={classNames(
      "relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors before:absolute before:-inset-2 before:content-[''] disabled:opacity-50 md:h-5 md:w-9 md:before:hidden",
      checked ? 'bg-brand' : 'bg-fg-faint/40',
    )}
  >
    <span
      className={classNames(
        'inline-block h-6 w-6 rounded-full bg-white shadow-e1 transition-transform md:h-4 md:w-4',
        checked ? 'translate-x-[22px] md:translate-x-[18px]' : 'translate-x-0.5',
      )}
    />
  </button>
);
